// Master Apps — design tokens (porte fiel de tokens.jsx do Claude Design).
// Linguagem visual: preto/branco minimalista com acento vermelho Masterboi.
// Dados densos, numéricos tabulares na sans da marca, cores de status semânticas.

export const NOC_RED = '#C8102E';
export const NOC_RED_DARK = '#A50D24';

export const fontSans = "'Gotham', 'Montserrat', system-ui, -apple-system, 'Segoe UI', sans-serif";

// ---------------------------------------------------------------------------
// Papéis tipográficos — leia antes de escolher fonte em qualquer ponto de uso.
//
// Histórico: a monoespaçada já foi a "fonte secundária geral" do app (número,
// rótulo em caixa alta e identificador, 200+ pontos de uso). Trocamos JetBrains
// Mono por IBM Plex Mono tentando amaciar a leitura e não resolveu — toda
// monoespaçada é quadrada por construção, o defeito era o PAPEL, não a família.
//
// Decisão vigente (não desfaça sem falar com o dono do produto): a mono ficou
// restrita a IDENTIFICADOR, onde ela é semântica. Número e rótulo usam a sans
// da marca. Use SEMPRE uma das três intenções abaixo em vez de montar
// `fontFamily` solto no componente — foi assim que a inconsistência nasceu.
// ---------------------------------------------------------------------------

/**
 * IDENTIFICADOR — a mono é SEMÂNTICA aqui, não decorativa: o leitor precisa
 * distinguir caractere por caractere e contar coluna.
 *
 * Use em: caminho de repositório (`masterboiteam/xxx`), slug, UUID, código de
 * aplicação (`APP-007`, ver `formatAppCode`), matrícula, CPF, chave/nome de
 * imagem, token, hash, e trecho de comando ou payload.
 *
 * NÃO use em número de tabela/gráfico nem em rótulo em caixa alta — para esses
 * existem `numeroTabular` e `rotuloCaixaAlta`.
 */
export const fontMono =
  "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * NÚMERO — valor, moeda, quantidade, contagem, data, porcentagem, tick de eixo.
 *
 * A sans da marca com figuras tabulares: alinha em coluna igual à mono (todo
 * dígito ocupa a mesma largura) sem o aspecto de código.
 *
 * Espalhe no início do objeto de estilo — `{ ...numeroTabular, fontSize: 12 }`.
 */
export const numeroTabular = {
  fontFamily: fontSans,
  fontVariantNumeric: 'tabular-nums',
} as const;

/**
 * RÓTULO EM CAIXA ALTA — cabeçalho de tabela, rótulo de stat tile, chip, badge.
 *
 * A sans da marca com entreletra aberta para manter a leitura de rótulo que a
 * mono dava "de graça".
 *
 * Espalhe no INÍCIO do objeto de estilo, para que um `letterSpacing` próprio do
 * ponto de uso venha depois e ganhe:
 * `{ ...rotuloCaixaAlta, letterSpacing: '0.14em' }`.
 * Nunca some/duplique entreletra — o valor do ponto de uso é o que vale.
 */
export const rotuloCaixaAlta = {
  fontFamily: fontSans,
  letterSpacing: '0.06em',
} as const;

// QUARTO CASO, sem token próprio de propósito: PROSA e CHIP DE CAIXA MISTA
// ("React 19", "Conforme", "TI · Financeiro", nome de time, nome de tecnologia,
// frase de rodapé). Boa parte dos usos antigos da mono era isto — mono como
// fonte secundária genérica, puramente decorativa.
//
// Esses pontos usam `fontFamily: fontSans` cru, e não um token: não há intenção
// a nomear, é só a fonte primária do app. NÃO aplique `rotuloCaixaAlta` neles —
// injetaria 0.06em de entreletra em texto de caixa mista, alargando chip com
// `whiteSpace: 'nowrap'` e apertando título que já estoura. E não remova o
// `fontFamily` "porque herda": drawer, popover e dropdown desenham via
// `createPortal`, fora da raiz que define a sans, e `<button>` não herda
// família — nesses o valor explícito é o que garante a fonte certa.

export type StatusTone = {
  fg: string;
  bg: string;
  solid: string;
  dim: string;
};

// Paleta de status — semântica.
export const STATUS: Record<string, StatusTone> = {
  ok: { fg: '#15803d', bg: '#dcfce7', solid: '#22c55e', dim: '#86efac' },
  warning: { fg: '#b45309', bg: '#fef3c7', solid: '#f59e0b', dim: '#fcd34d' },
  average: { fg: '#9a3412', bg: '#ffedd5', solid: '#f97316', dim: '#fdba74' },
  high: { fg: '#b91c1c', bg: '#fee2e2', solid: '#ef4444', dim: '#fca5a5' },
  disaster: { fg: '#7f1d1d', bg: '#fee2e2', solid: '#dc2626', dim: '#f87171' },
  info: { fg: '#1d4ed8', bg: '#dbeafe', solid: '#3b82f6', dim: '#93c5fd' },
  unknown: { fg: '#475569', bg: '#f1f5f9', solid: '#94a3b8', dim: '#cbd5e1' },
};

export interface Palette {
  bg: string;
  surface: string;
  surface2: string;
  surface3: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  muted: string;
  placeholder: string;
  inputBg: string;
  chartGrid: string;
  chartAxis: string;
  ring: string;
}

// Levanta a luminosidade de uma cor apenas no dark mode — preserva matiz &
// saturação, então o vermelho Masterboi continua vermelho (só mais vivo) em
// vez de afundar no fundo quase-preto. No-op no light ou para cores já claras.
export function liftForDark(hex: string, dark: boolean, targetL = 0.56): string {
  if (!dark || typeof hex !== 'string' || !hex.startsWith('#') || hex.length < 7) return hex;
  let r = Number.parseInt(hex.slice(1, 3), 16) / 255;
  let g = Number.parseInt(hex.slice(3, 5), 16) / 255;
  let b = Number.parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  // Cinza (max === min) fica com o matiz e a saturação neutros já inicializados.
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h /= 6;
  }
  if (l >= targetL) return hex;
  l = targetL;
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const toHex = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, '0');
  r = hue2rgb(p, q, h + 1 / 3);
  g = hue2rgb(p, q, h);
  b = hue2rgb(p, q, h - 1 / 3);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function darken(hex: string, pct = 0.14): string {
  const h = hex.replace('#', '');
  const r = Number.parseInt(h.slice(0, 2), 16);
  const g = Number.parseInt(h.slice(2, 4), 16);
  const b = Number.parseInt(h.slice(4, 6), 16);
  const f = (v: number) => Math.max(0, Math.min(255, Math.round(v * (1 - pct))));
  return `#${[f(r), f(g), f(b)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

export function makePalette(dark: boolean): Palette {
  return {
    bg: dark ? '#0a0a0b' : '#fafaf9',
    surface: dark ? '#111114' : '#ffffff',
    surface2: dark ? '#16171b' : '#f5f5f4',
    surface3: dark ? '#1c1d22' : '#e7e5e4',
    border: dark ? '#26272d' : '#e7e5e4',
    borderStrong: dark ? '#3a3b42' : '#d6d3d1',
    text: dark ? '#fafaf9' : '#0a0a0b',
    textMuted: dark ? '#d6d3d1' : '#44403c',
    muted: dark ? '#a8a29e' : '#78716c',
    placeholder: dark ? '#57534e' : '#a8a29e',
    inputBg: dark ? '#16171b' : '#ffffff',
    chartGrid: dark ? '#26272d' : '#e7e5e4',
    chartAxis: dark ? '#57534e' : '#a8a29e',
    ring: 'rgba(200, 16, 46, 0.18)',
  };
}

// Props compartilhadas por todas as telas (espelha o screenProps do App() HTML).
export interface ScreenProps {
  dark: boolean;
  accent: string;
  accentDark: string;
}
